import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const STATUS_LABELS = {
  pending: 'En attente',
  arrived: 'Livreur sur place',
  in_progress: 'Livraison en cours',
  delivered: 'Livré',
  skipped: 'Passé',
  problem: 'Problème signalé',
};

const STATUS_COLORS = {
  pending: '#64748b',
  arrived: '#f59e0b',
  in_progress: '#3b82f6',
  delivered: '#10b981',
  skipped: '#9ca3af',
  problem: '#ef4444',
};

const POLL_INTERVAL = 15000;

/**
 * Public tracking page — no auth required.
 * Accessible via /track/:token
 */
export default function TrackingPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let interval;
    const fetchData = async () => {
      try {
        const res = await fetch(`/api/track/${token}`);
        if (!res.ok) {
          setError(res.status === 404 ? 'Lien de suivi invalide ou expiré.' : 'Erreur de chargement.');
          return;
        }
        const json = await res.json();
        setData(json);
        setError('');
      } catch (_err) {
        setError('Impossible de charger les informations.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    interval = setInterval(fetchData, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin h-10 w-10 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-2xl p-8 shadow-sm text-center max-w-sm">
          <p className="text-4xl mb-4">📦</p>
          <p className="text-gray-800 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const eta = data.estimatedArrival
    ? new Date(data.estimatedArrival).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    : null;
  const isDelivered = data.stopStatus === 'delivered';
  const statusColor = STATUS_COLORS[data.stopStatus] || '#64748b';
  const progressPct = data.progress.total > 0
    ? Math.round((data.progress.completed / data.progress.total) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#0f172a] to-[#1e40af] text-white px-5 py-5 text-center">
        <p className="text-xs uppercase tracking-wider opacity-70 mb-1">Suivi de livraison</p>
        <h1 className="text-xl font-bold">{data.clientName}</h1>
        <p className="text-sm opacity-80 mt-1">
          {new Date(data.deliveryDate).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>
      </header>

      <main className="max-w-md mx-auto p-4 space-y-4 -mt-2">
        {/* Status card */}
        <div className="bg-white rounded-2xl p-5 shadow-sm text-center">
          <div
            className="inline-block px-4 py-2 rounded-full text-white font-semibold text-sm mb-3"
            style={{ backgroundColor: statusColor }}
          >
            {STATUS_LABELS[data.stopStatus] || data.stopStatus}
          </div>

          {isDelivered ? (
            <div>
              <p className="text-4xl mb-2">✅</p>
              <p className="text-lg font-bold text-green-700">Livraison terminée !</p>
              <p className="text-sm text-gray-500 mt-1">
                {data.unitsCount} unité(s) livrée(s)
                {data.actualArrival && ` — arrive a ${new Date(data.actualArrival).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`}
              </p>
            </div>
          ) : (
            <div>
              {eta && (
                <div className="mb-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Arrivée estimée</p>
                  <p className="text-3xl font-bold text-[#0f172a]">{eta}</p>
                </div>
              )}
              <p className="text-sm text-gray-500">
                {data.unitsCount} unité(s) à livrer
              </p>
            </div>
          )}
        </div>

        {/* Driver info */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm">
              {(data.driverName || '?')[0].toUpperCase()}
            </div>
            <div>
              <p className="font-medium text-sm text-gray-800">{data.driverName}</p>
              <p className="text-xs text-gray-500">Votre livreur</p>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <div className="flex justify-between items-center mb-2">
            <p className="text-xs font-medium text-gray-500 uppercase">Progression de la tournée</p>
            <p className="text-sm font-bold text-gray-700">{data.progress.completed}/{data.progress.total}</p>
          </div>
          <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 rounded-full transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          {!isDelivered && data.stopOrder > 0 && (
            <p className="text-xs text-gray-400 mt-2 text-center">
              Vous êtes l'arrêt #{data.stopOrder} sur {data.progress.total}
            </p>
          )}
        </div>

        {/* Address */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <p className="text-xs font-medium text-gray-500 uppercase mb-1">Adresse de livraison</p>
          <p className="text-sm text-gray-800">{data.clientAddress}</p>
          {data.clientCity && <p className="text-sm text-gray-500">{data.clientCity}</p>}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 pt-2">
          Velogistique — Suivi automatique
        </p>
      </main>
    </div>
  );
}
